import React, { useRef, useState, useEffect } from 'react';
import './VideoSection.css';

const AppleVideoExperience = () => {
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const handleScroll = () => {
      if (!videoSectionRef.current) return;

      const rect = videoSectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;

      // Calculate when the section is in view
      const isInView = sectionTop < windowHeight && sectionTop + sectionHeight > 0;

      if (isInView) {
        // When section is in the center of viewport, scale is 1
        // As it moves up, scale decreases
        const centerPoint = windowHeight / 2;
        const sectionCenter = sectionTop + sectionHeight / 2;
        const distanceFromCenter = Math.abs(sectionCenter - centerPoint);
        const maxDistance = windowHeight;
        
        // Calculate scale based on distance from center
        let newScale = 1 - (distanceFromCenter / maxDistance) * 0.6;
        newScale = Math.max(0.4, Math.min(1, newScale));
        
        setScale(newScale);
      } else if (sectionTop > windowHeight) {
        // Section is below viewport
        setScale(1);
      } else {
        // Section is above viewport
        setScale(0.4);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle play/pause
  const handlePauseClick = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPaused(false);
    } else {
      videoRef.current.pause();
      setIsPaused(true);
    }
  };

  // Toggle mute/unmute
  const handleSoundClick = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };

  // Toggle full-screen
  const handleFullScreenClick = () => {
    if (!videoRef.current) return;

    if (!isFullScreen) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.mozRequestFullScreen) {
        videoRef.current.mozRequestFullScreen();
      }
      setIsFullScreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      }
      setIsFullScreen(false);
    }
  };

  return (
    <div className="apple-video-experience">
      {/* Hero Section */}
      <section className="hero-section-video">
        <div className="hero-content-video">
          <h1 className="hero-title-video">
            Build Mental
            <span className="hero-subtitle-video">Resilience</span>
          </h1>
          <p className="hero-description-video">
            Discover how WhiteMatter's AI-powered neurofeedback transforms mental wellness through Self-Directed Neuroplasticity
          </p>
          <div className="scroll-indicator">
            <svg className="scroll-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section ref={videoSectionRef} className="video-section">
        <div 
          className="video-container"
          style={{ transform: `scale(${scale})` }}
        >
          <div className="video-card">
            {/* The video */}
            <video
              ref={videoRef}
              src="https://whitematter-video-htdmb7bfgjh4gkb6.z01.azurefd.net/whitemattervideo/whitematter.mp4"
              playsInline
              autoPlay
              loop
              muted={isMuted}
              controls={false}
              className="video-element"
            />

            {/* Custom minimal controls */}
            <div className="video-controls">
              {/* Play/Pause */}
              <button className="control-button" onClick={handlePauseClick}>
                {isPaused ? (
                  <svg className="control-icon play-icon\" fill="currentColor\" viewBox="0 0 20 20">
                    <path fillRule="evenodd\" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z\" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="control-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 002 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
              </button>

              {/* Sound (mute/unmute) */}
              <button className="control-button" onClick={handleSoundClick}>
                {isMuted ? (
                  <svg className="control-icon\" fill="currentColor\" viewBox="0 0 20 20">
                    <path fillRule="evenodd\" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.215 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.215l4.168-3.793a1 1 0 011.617.793zm-4.383 5.224L7 9.793V10.207L5 11.699 5 8.301z\" clipRule="evenodd" />
                    <path d="M12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" />
                  </svg>
                ) : (
                  <svg className="control-icon" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.215 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.215l4.168-3.793a1 1 0 011.617.793zM7.707 7.293L10 5l-2.293 2.293A1 1 0 006.993 7H2v6h4.993a1 1 0 00.714.293L10 15V5z" clipRule="evenodd" />
                    <path d="M11.609 7.219a.5.5 0 01.704.063A4.5 4.5 0 0114.5 10a4.5 4.5 0 01-2.187 3.718.5.5 0 11-.532-.858A3.5 3.5 0 0013.5 10c0-1.25-.65-2.35-1.719-2.98a.5.5 0 01.063-.704zm2.17-2.035a.5.5 0 01.704.064 8.5 8.5 0 010 11.494.5.5 0 11-.768-.64 7.5 7.5 0 000-10.214.5.5 0 01.064-.704z" />
                  </svg>
                )}
              </button>

              {/* Full-screen */}
              <button className="control-button" onClick={handleFullScreenClick}>
                <svg className="control-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="content-section-video">
        <div className="content-container">
          <h2 className="content-title">
            AI-Powered
            <span className="content-subtitle">Neurofeedback</span>
          </h2>
          <p className="content-description">
            WhiteMatter integrates with occupational health standards to provide employers actionable insights that balance workloads and drive performance through lasting mental resilience.
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon performance">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="feature-title">Self-Directed Neuroplasticity</h3>
              <p className="feature-text">Harness your brain's natural ability to adapt and build lasting mental resilience through targeted neurofeedback training</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon experience">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="feature-title">Workplace Integration</h3>
              <p className="feature-text">Seamlessly integrates with occupational health standards to provide employers with actionable insights for optimal team performance</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon quality">
                <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="feature-title">Performance Analytics</h3>
              <p className="feature-text">Data-driven insights help balance workloads and optimize mental wellness for sustained high performance across teams</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AppleVideoExperience;